import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_STATUS_LABELS,
  type FeedbackCategory,
  type FeedbackStatus,
  getAdminFeedbackList,
} from "@/api/feedback";

const STATUSES: FeedbackStatus[] = [
  "open",
  "in_progress",
  "resolved",
  "wontfix",
  "orphaned",
];

const CATEGORIES: FeedbackCategory[] = [
  "incorrect_translation",
  "abbreviation_issue",
  "missing_terminology",
  "morphology_grammar",
  "style_register",
  "redundant_addition",
  "omission",
  "other",
];

const PAGE_SIZE = 50;

export default function AdminFeedback() {
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as FeedbackStatus | null) ?? undefined;
  const category =
    (params.get("category") as FeedbackCategory | null) ?? undefined;
  const author = params.get("author") || undefined;
  const draftId = params.get("draft_id") || undefined;
  const q = params.get("q") || undefined;
  const offset = Number(params.get("offset") ?? 0) || 0;

  const query = useQuery({
    queryKey: [
      "admin-feedback",
      status,
      category,
      author,
      draftId,
      q,
      offset,
    ],
    queryFn: async () => {
      const r = await getAdminFeedbackList({
        status,
        category,
        author,
        draft_id: draftId,
        q,
        limit: PAGE_SIZE,
        offset,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    refetchInterval: 30_000,
  });

  function setFilter(key: string, value: string | undefined): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("offset", "0");
    setParams(next, { replace: true });
  }
  function setOffset(o: number): void {
    const next = new URLSearchParams(params);
    next.set("offset", String(o));
    setParams(next, { replace: true });
  }
  function resetFilters(): void {
    setParams(new URLSearchParams(), { replace: true });
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1200 }}>
      <header style={{ marginBottom: "var(--sp-3)" }}>
        <h1>Замечания к переводам</h1>
        <p className="mt-text-tight mt-muted">
          Inline-комментарии reviewer'ов из /translate. Можно фильтровать
          по статусу/категории и переходить в feedback detail для
          разбора (создать запрещённую форму, отправить candidate в
          ревью и т.п.).
        </p>
      </header>

      <div
        className="rl-filters"
        style={{ marginBottom: "var(--sp-3)", flexWrap: "wrap" }}
      >
        <div className="rl-filters__group">
          <span className="rl-filters__label">Статус</span>
          <div className="mt-segmented">
            <button
              type="button"
              className={
                "mt-segmented__item" + (!status ? " is-active" : "")
              }
              onClick={() => setFilter("status", undefined)}
            >
              Все
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  "mt-segmented__item" + (status === s ? " is-active" : "")
                }
                onClick={() => setFilter("status", s)}
              >
                {FEEDBACK_STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
        <div className="rl-filters__sep" />
        <div className="rl-filters__group">
          <span className="rl-filters__label">Категория</span>
          <select
            className="mt-input mt-input--sm"
            value={category ?? ""}
            onChange={(e) =>
              setFilter("category", e.target.value || undefined)
            }
          >
            <option value="">все</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {FEEDBACK_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div className="rl-filters__sep" />
        <div className="rl-filters__group">
          <span className="rl-filters__label">Поиск</span>
          <input
            className="mt-input mt-input--sm"
            type="text"
            placeholder="по выделенному тексту / комментарию"
            defaultValue={q ?? ""}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setFilter(
                  "q",
                  (e.target as HTMLInputElement).value.trim() || undefined,
                );
              }
            }}
            style={{ minWidth: 240 }}
          />
        </div>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <button
          type="button"
          className="mt-btn mt-btn--ghost mt-btn--sm"
          onClick={resetFilters}
        >
          Сбросить
        </button>
      </div>

      {query.isLoading ? (
        <div className="mt-empty">Загружаем…</div>
      ) : items.length === 0 ? (
        <div className="mt-empty">
          Замечаний нет. Начни с /translate — выдели фрагмент и нажми
          «Замечание».
        </div>
      ) : (
        <>
          <div
            className="mt-table"
            style={{
              border: "1px solid var(--border-default, #d0d5dd)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg-soft, #f6f7f9)" }}>
                  <Th>Дата</Th>
                  <Th>Автор</Th>
                  <Th>Сторона</Th>
                  <Th>Категории</Th>
                  <Th>Фрагмент</Th>
                  <Th>Статус</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.feedback_id}
                    style={{
                      borderTop: "1px solid var(--border-default, #d0d5dd)",
                    }}
                  >
                    <Td>{it.created_at?.slice(0, 16).replace("T", " ")}</Td>
                    <Td>{it.author}</Td>
                    <Td>
                      <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
                        {it.side === "source" ? "ориг" : "перевод"}
                      </span>
                    </Td>
                    <Td>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 3,
                        }}
                      >
                        {it.categories.map((c) => (
                          <span
                            key={c}
                            className="mt-badge mt-badge--info mt-badge--no-dot"
                            style={{ fontSize: 10 }}
                          >
                            {FEEDBACK_CATEGORY_LABELS[c]}
                          </span>
                        ))}
                      </div>
                    </Td>
                    <Td style={{ maxWidth: 320 }}>
                      <Link
                        to={`/admin/feedback/${encodeURIComponent(it.feedback_id)}`}
                        style={{
                          color: "var(--text)",
                          textDecoration: "none",
                          fontStyle: "italic",
                        }}
                        title={it.selected_text}
                      >
                        «
                        {it.selected_text.length > 70
                          ? it.selected_text.slice(0, 68) + "…"
                          : it.selected_text}
                        »
                      </Link>
                      {it.comment_text && (
                        <div
                          style={{
                            color: "var(--text-soft)",
                            fontSize: 12,
                          }}
                        >
                          {it.comment_text.length > 90
                            ? it.comment_text.slice(0, 88) + "…"
                            : it.comment_text}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <span
                        className={
                          "mt-badge mt-badge--no-dot " +
                          (it.status === "resolved"
                            ? "mt-badge--success"
                            : it.status === "wontfix"
                              ? "mt-badge--neutral"
                              : it.status === "orphaned"
                                ? "mt-badge--warning"
                                : "mt-badge--info")
                        }
                      >
                        {FEEDBACK_STATUS_LABELS[it.status]}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "var(--sp-3) 0",
            }}
          >
            <span className="mt-text-tight mt-muted">
              {offset + 1}–{offset + items.length} из {total}
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--ghost"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← назад
            </button>
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--ghost"
              disabled={offset + items.length >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              вперёд →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "8px 10px",
        fontWeight: "var(--fw-semi, 600)",
        fontSize: 12,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "var(--text-soft)",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <td style={{ padding: "8px 10px", verticalAlign: "top", ...(style ?? {}) }}>
      {children}
    </td>
  );
}
